/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { createEntity } from 'jotai/entity';
import { OriginType, RunType, SanctioningBodyEnum } from 'models/viewModels';
import { SystemWorkspaceId, systemWorkspaceIdSlug } from '../SystemWorkspaceId';
import { WorkspaceObject } from '../WorkspaceObject';

export const defaultRace: WorkspaceObject = {
  id: SystemWorkspaceId.DefaultRace,
  name: 'Default Race',
  slug: systemWorkspaceIdSlug.defaultRace,
  owner: 'system',
  sessionTypes: [RunType.RACE],
  sanctioningBody: SanctioningBodyEnum.NASCAR,
  activeView: 't5',
  views: {
    byId: {
      t0: {
        id: 't0',
        name: 'Race View',
        visualizations: ['v1', 'v6', 'v8', 'v9', 'v11', 'v14', 'v19', '50428cf4-8ed8-426b-aadb-71186ad8b722'],
        layouts: 'vl0',
      },
      t1: {
        id: 't1',
        name: 'Weather',
        visualizations: ['v2', 'v3', 'v17'],
        layouts: 'vl1',
      },
      t2: {
        id: 't2',
        name: 'Track Temp',
        visualizations: ['v4'],
        layouts: 'vl4',
      },
      t3: {
        id: 't3',
        name: 'Pit Road Summary',
        visualizations: ['v7'],
        layouts: 'vl3',
      },
      t4: {
        id: 't4',
        name: 'Report',
        visualizations: ['v10'],
        layouts: 'vl5',
      },
      t5: {
        id: 't5',
        name: 'Combined View',
        visualizations: ['v12', 'v13', 'v16', 'dd80058e-7c84-42be-9454-b7d89796cc93'],
        layouts: 'vl6',
      },
      t6: {
        id: 't6',
        name: 'Fast Laps',
        visualizations: ['v18'],
        layouts: 'vl8',
      },
    },
    allIds: ['t5', 't0', 't4', 't1', 't2', 't3', 't6'],
  },
  viewLayouts: {
    byId: {
      vl0: [
        {
          w: 4.666666666666667,
          h: 24,
          x: 7.333333333333333,
          y: 0,
          i: 'v1',
          moved: false,
          static: false,
        },
        {
          w: 6.8,
          h: 27,
          x: 3,
          y: 24,
          i: 'v6',
          moved: false,
          static: false,
        },
        {
          w: 3,
          h: 26,
          x: 0,
          y: 0,
          i: 'v8',
          moved: false,
          static: false,
        },
        {
          w: 3,
          h: 16,
          x: 0,
          y: 26,
          i: 'v9',
          moved: false,
          static: false,
        },
        {
          w: 2.2,
          h: 15,
          x: 9.8,
          y: 24,
          i: 'v11',
          moved: false,
          static: false,
        },
        {
          w: 3,
          h: 9,
          x: 0,
          y: 42,
          i: 'v14',
          moved: false,
          static: false,
        },
        {
          w: 2.2,
          h: 12,
          x: 9.8,
          y: 39,
          i: 'v19',
          moved: false,
          static: false,
        },
        {
          w: 4.333333333333333,
          h: 24,
          x: 3,
          y: 0,
          i: '50428cf4-8ed8-426b-aadb-71186ad8b722',
          moved: false,
          static: false,
        },
      ],
      vl1: [
        {
          w: 6,
          h: 26,
          x: 0,
          y: 0,
          i: 'v2',
          moved: false,
          static: false,
        },
        {
          w: 6,
          h: 50,
          x: 6,
          y: 0,
          i: 'v3',
          moved: false,
          static: false,
        },
        {
          w: 6,
          h: 24,
          x: 0,
          y: 26,
          i: 'v17',
          moved: false,
          static: false,
        },
      ],
      vl3: [
        {
          w: 12,
          h: 50,
          x: 0,
          y: 0,
          i: 'v7',
          moved: false,
          static: false,
        },
      ],
      vl4: [
        {
          w: 12,
          h: 50,
          x: 0,
          y: 0,
          i: 'v4',
          moved: false,
          static: false,
        },
      ],
      vl5: [
        {
          w: 12,
          h: 50,
          x: 0,
          y: 0,
          i: 'v10',
          moved: false,
          static: false,
        },
      ],
      vl6: [
        {
          w: 8.333333333333334,
          h: 30,
          x: 3.6666666666666667,
          y: 21,
          i: 'v12',
          moved: false,
          static: false,
        },
        {
          w: 3.6666666666666667,
          h: 36,
          x: 0,
          y: 0,
          i: 'v13',
          moved: false,
          static: false,
        },
        {
          w: 3.6666666666666667,
          h: 15,
          x: 0,
          y: 36,
          i: 'v16',
          moved: false,
          static: false,
        },
        {
          w: 8.333333333333334,
          h: 21,
          x: 3.6666666666666667,
          y: 0,
          i: 'dd80058e-7c84-42be-9454-b7d89796cc93',
          moved: false,
          static: false,
        },
      ],
      vl7: [],
      vl8: [
        {
          w: 12,
          h: 50,
          x: 0,
          y: 0,
          i: 'v18',
          moved: false,
          static: false,
        },
      ],
    },
    allIds: ['vl0', 'vl1', 'vl3', 'vl4', 'vl5', 'vl6', 'vl7', 'vl8'],
  },
  visualizations: {
    byId: {
      v1: {
        id: 'v1',
        name: 'Track View Map',
        type: 'trackViewMap',
        settings: 'vs1',
      },
      v2: {
        id: 'v2',
        name: 'Weather',
        type: 'weather',
        settings: 'vs2',
      },
      v3: {
        id: 'v3',
        name: 'Weather Radar Map',
        type: 'weatherMap',
        settings: 'vs3',
      },
      v4: {
        id: 'v4',
        name: 'Track Temperature',
        type: 'trackTemperature',
        settings: 'vs4',
      },
      v6: {
        id: 'v6',
        name: 'Lap Times Graph',
        type: 'lapTimesGraph',
        settings: 'vs6',
      },
      v7: {
        id: 'v7',
        name: 'Summary Table',
        type: 'pitRoadSummaryTable',
        settings: 'vs7',
      },
      v8: {
        id: 'v8',
        name: 'Race Order Table',
        type: 'raceOrderTable',
        settings: 'vs8',
      },
      v9: {
        id: 'v9',
        name: 'Global Competitor Groups',
        type: 'competitorGroupsDock',
        settings: 'vs9',
      },
      v10: {
        id: 'v10',
        name: 'Rank Analysis Report',
        type: 'rankAnalysisReport',
        settings: 'vs14',
      },
      v11: {
        id: 'v11',
        name: 'Audio Transcription',
        type: 'audioTranscription',
        settings: 'vs15',
      },
      v12: {
        id: 'v12',
        name: 'Lap Times Graph',
        type: 'lapTimesGraph',
        settings: 'vs16',
      },
      v13: {
        id: 'v13',
        name: 'Race Order Table',
        type: 'raceOrderTable',
        settings: 'vs17',
      },
      v14: {
        id: 'v14',
        name: 'Summary Table',
        type: 'pitRoadSummaryTable',
        settings: 'vs18',
      },
      v16: {
        id: 'v16',
        name: 'Global Competitor Groups',
        type: 'competitorGroupsDock',
        settings: 'vs20',
      },
      v17: {
        id: 'v17',
        name: 'Forecast',
        type: 'weather',
        settings: 'vs21',
      },
      v18: {
        id: 'v18',
        name: 'Fastest 10-20-30',
        type: 'fastestLapsTable',
        settings: 'vs22',
      },
      v19: {
        id: 'v19',
        name: 'Audio Transcription',
        type: 'audioTranscription',
        settings: 'vs23',
      },
      'dd80058e-7c84-42be-9454-b7d89796cc93': {
        id: 'dd80058e-7c84-42be-9454-b7d89796cc93',
        name: 'Lap Times Table',
        type: 'lapTimesTable',
        settings: 'c53f67c2-2e16-4a41-ba49-25f6d20dc62b',
      },
      '50428cf4-8ed8-426b-aadb-71186ad8b722': {
        id: '50428cf4-8ed8-426b-aadb-71186ad8b722',
        name: 'Lap Times Table',
        type: 'lapTimesTable',
        settings: 'c8aede62-44d1-4714-9450-a4348a62e81a',
      },
    },
    allIds: [
      'v1',
      'v2',
      'v3',
      'v4',
      'v6',
      'v7',
      'v8',
      'v9',
      'v10',
      'v11',
      'v12',
      'v13',
      'v14',
      'v16',
      'v17',
      'v18',
      'v19',
      'dd80058e-7c84-42be-9454-b7d89796cc93',
      '50428cf4-8ed8-426b-aadb-71186ad8b722',
    ],
  },
  visualizationSettings: {
    byId: {
      vs1: {
        id: 'vs1',
        showLeaderIndicator: true,
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.ERDP,
      },
      vs2: {
        id: 'vs2',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        focusedTab: 2,
      },
      vs3: {
        id: 'vs3',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        radiusRings: false,
      },
      vs4: {
        id: 'vs4',
        columnStates: [
          {
            colId: 'cameraTakeTimestamp',
            flex: 1,
            sort: 'desc',
          },
          {
            colId: 'cameraLocation',
            flex: 1,
          },
          {
            colId: 'lowLineTemperature',
            flex: 1,
          },
          {
            colId: 'middleLineTemperature',
            flex: 1,
          },
          {
            colId: 'highLineTemperature',
            flex: 1,
          },
          {
            colId: 'referenceTemperature',
            flex: 1,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffDrivers: 'none',
          selectedPlayoffTopDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        showHeatMap: true,
        showGraph: true,
        showTable: true,
        isLive: false,
      },
      vs6: {
        id: 'vs6',
        lapsFrom: 1,
        lapsAutoIncrement: false,
        timeofDayEnabled: false,
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
        timeThreshold: 35,
        lastEventIdUsed: 5303,
        isInlineSettingsShown: true,
        isVehicleLegendShown: true,
        timeThresholdMaximum: 32.3,
        timeThresholdMinimum: 28.8,
        timeInterval: 0.5,
      },
      vs7: {
        id: 'vs7',
        dataFeedSelections: OriginType.ERDP,
        columnStates: [
          {
            colId: 'driverFullName',
            width: 135,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'vehicleNumber',
            width: 111,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'lap',
            width: 85,
            hide: false,
            pinned: null,
            sort: 'desc',
            sortIndex: 0,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'flagIn',
            width: 87,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'pitStopType',
            width: 94,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'totalTime',
            width: 100,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'missedEvents',
            width: 79,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'driverTime',
            width: 101,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'crewTime',
            width: 113,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'leaderLap',
            width: 159,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'vehicleStopToJack',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'rightTime',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'jackTime',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'leftTime',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'jackToVehicleGoes',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'flagOut',
            width: 138,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitInLap',
            width: 138,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitInPosition',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitOutPosition',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitPositionDifference',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'avgPitInPosition',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'avgPitOutPosition',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'avgPitPositionDifference',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'eventName',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'sessionDate',
            width: 200,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'greenIn',
            width: 138,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'greenOut',
            width: 138,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitOutTime',
            width: 138,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitInTime',
            width: 135.06999999999995,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'driverGoes',
            width: 116,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'driverEnter',
            width: 116,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffDrivers: 'none',
          selectedPlayoffTopDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        lineCrossingSelections: {
          '4': {
            greenIn: 'L5',
            greenOut: 'L4',
          },
          '22': {
            greenIn: 'L4',
            greenOut: 'L4',
          },
          '26': {},
          '43': {},
          '82': {
            greenIn: 'L11',
            greenOut: 'L8',
          },
          '84': {},
          '105': {
            greenIn: 'L14',
            greenOut: 'L8',
          },
          '133': {},
          '138': {},
          '162': {
            greenIn: 'L7',
            greenOut: 'L9',
          },
          '177': {
            greenIn: 'L2',
            greenOut: 'L5',
          },
          '218': {},
        },
        lastEventIdUsed: 5303,
      },
      vs8: {
        id: 'vs8',
        columnStates: [
          {
            colId: 'runningPosition',
            width: 50,
            hide: false,
            pinned: null,
            sort: 'asc',
            sortIndex: 0,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'driverFullName',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'movingAverageLapTime',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lapsLed',
            width: 50,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'vehicleNumber',
            width: 72,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lapsCompleted',
            width: 64,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'deltaToTarget',
            width: 100,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lastLapTime',
            width: 95,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'bestLapTime',
            width: 70,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'runName',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'ppLap',
            width: 39,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'ppStrat',
            width: 38.49599999999998,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitLap',
            width: 75,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
      },
      vs9: {
        id: 'vs9',
      },
      vs10: {
        id: 'vs10',
      },
      vs11: {
        id: 'vs11',
        pointsCalculationType: 0,
        positionProjectionType: 2,
        gapType: 1,
        gapValue: 16,
        cutoffRank: 10,
        lastPlacePlayoffs: 10,
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        columnStates: [
          {
            colId: 'currentWins',
            hide: true,
          },
        ],
      },
      vs12: {
        id: 'vs12',
        roles: {
          Driver: true,
          CrewChief: true,
          Spotter: true,
          Guest: true,
          Unidentified: true,
        },
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
      },
      vs13: {
        id: 'vs13',
        roles: {
          Driver: true,
          CrewChief: true,
          Spotter: true,
          Guest: true,
          Unidentified: true,
        },
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
      },
      vs14: {
        id: 'vs14',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
        sortType: 'vehicleNumber',
        slowThresholdColor: 'highlight-yellow-200',
        slowThresholdColorPallete: 'highlight',
        slowColorPallete: 'highlight',
        slowColor: 'highlight-yellow-400',
        mediumColor: 'highlight-green-400',
        mediumColorPallete: 'highlight',
        fastColor: 'highlight-green-600',
        fastColorPallete: 'highlight',
        isSlowThresholdColorEnabled: true,
        isSlowColorEnabled: false,
        isMediumColorEnabled: true,
        isFastColorEnabled: true,
        xLap: -1,
        lastEventIdUsed: 5303,
        threshold: 3000,
        lastTimeUpdated: 1693825975757,
      },
      vs15: {
        id: 'vs15',
        roles: {
          Driver: true,
          CrewChief: true,
          Spotter: true,
          Unidentified: true,
        },
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: ['20'],
        },
      },
      vs16: {
        id: 'vs16',
        lapsFrom: 1,
        lapsAutoIncrement: false,
        timeofDayEnabled: false,
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'oem',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
        isInlineSettingsShown: true,
        isVehicleLegendShown: true,
        lastEventIdUsed: 5303,
        timeInterval: 0.5,
        timeThresholdMaximum: 32.3,
        timeThresholdMinimum: 28.8,
      },
      vs17: {
        id: 'vs17',
        columnStates: [
          {
            colId: 'runningPosition',
            width: 50,
            hide: false,
            pinned: null,
            sort: 'asc',
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'driverFullName',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'runName',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'vehicleNumber',
            width: 67,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'lapsCompleted',
            width: 70,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'deltaToTarget',
            width: 100,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lastLapTime',
            width: 103,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'bestLapTime',
            width: 101,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lapsLed',
            width: 73,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'ppStrat',
            width: 81,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'ppLap',
            width: 77.32000000000005,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'movingAverageLapTime',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
      },
      vs18: {
        id: 'vs18',
        dataFeedSelections: OriginType.ERDP,
        columnStates: [
          {
            colId: 'driverFullName',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lap',
            width: 78,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'leaderLap',
            width: 52,
            hide: false,
            pinned: null,
            sort: 'desc',
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'vehicleNumber',
            width: 52,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitStopType',
            width: 52,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitInTime',
            width: 81,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitOutTime',
            width: 84,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'totalTime',
            width: 70,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'driverEnter',
            width: 87,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'vehicleStopToJack',
            width: 90,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'rightTime',
            width: 94,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'jackTime',
            width: 98,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'leftTime',
            width: 102,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'jackToVehicleGoes',
            width: 107,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'driverGoes',
            width: 112,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'flagIn',
            width: 118,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'flagOut',
            width: 124,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'missedEvents',
            width: 131,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitInLap',
            width: 138,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitInPosition',
            width: 147,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitOutPosition',
            width: 157,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'avgPitInPosition',
            width: 168,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'avgPitOutPosition',
            width: 181,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'avgPitPositionDifference',
            width: 196,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'eventName',
            width: 214,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'sessionDate',
            width: 235,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'driverTime',
            width: 70,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'crewTime',
            width: 70,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'pitPositionDifference',
            width: 52,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'greenIn',
            width: 75,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'greenOut',
            width: 75,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        lineCrossingSelections: {
          '4': {},
          '43': {},
        },
        lastEventIdUsed: '5303',
      },
      vs20: {
        id: 'vs20',
      },
      vs21: {
        id: 'vs21',
        focusedTab: 1,
      },
      vs22: {
        id: 'vs22',
        columnStates: [
          {
            colId: 'fastLapRank',
          },
          {
            colId: 'vehicleNumber',
          },
          {
            colId: 'vehicleManufacturer',
          },
          {
            colId: 'fastLap',
          },
          {
            colId: 'fastLapDiff',
          },
          {
            colId: 'fast10VehicleNumber',
          },
          {
            colId: 'fast10VehicleManufacturer',
          },
          {
            colId: 'fast10',
          },
          {
            colId: 'fast10Diff',
          },
          {
            colId: 'fast20VehicleNumber',
          },
          {
            colId: 'fast20VehicleManufacturer',
          },
          {
            colId: 'fast20',
          },
          {
            colId: 'fast20Diff',
          },
          {
            colId: 'fast30VehicleNumber',
          },
          {
            colId: 'fast30VehicleManufacturer',
          },
          {
            colId: 'fast30',
          },
          {
            colId: 'fast30Diff',
          },
          {
            colId: 'fastXVehicleNumber',
          },
          {
            colId: 'fastXVehicleManufacturer',
          },
          {
            colId: 'fastX',
          },
          {
            colId: 'fastXDiff',
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffDrivers: 'none',
          selectedPlayoffTopDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        topXLaps: 50,
        lastEventIdUsed: 5303,
      },
      vs23: {
        id: 'vs23',
        roles: {
          Driver: true,
          CrewChief: true,
          Spotter: true,
          Unidentified: true,
        },
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: ['17'],
        },
      },
      'c53f67c2-2e16-4a41-ba49-25f6d20dc62b': {
        id: 'c53f67c2-2e16-4a41-ba49-25f6d20dc62b',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        columnStates: [],
        dataFeedSelections: OriginType.MULTILOOP,
        localVehicleSelectionsSortOrder: [],
        sortOrder: ['1', '3', '5', '8', '9', '24', '48', '99'],
      },
      'c8aede62-44d1-4714-9450-a4348a62e81a': {
        id: 'c8aede62-44d1-4714-9450-a4348a62e81a',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        columnStates: [],
        dataFeedSelections: OriginType.MULTILOOP,
        localVehicleSelectionsSortOrder: [],
        sortOrder: ['1', '3', '5', '8', '9', '24', '48', '99'],
      },
    },
    allIds: [
      'vs1',
      'vs2',
      'vs3',
      'vs4',
      'vs6',
      'vs7',
      'vs8',
      'vs9',
      'vs10',
      'vs11',
      'vs12',
      'vs13',
      'vs14',
      'vs15',
      'vs16',
      'vs17',
      'vs18',
      'vs20',
      'vs21',
      'vs22',
      'vs23',
      'c53f67c2-2e16-4a41-ba49-25f6d20dc62b',
      'c8aede62-44d1-4714-9450-a4348a62e81a',
    ],
  },
  selectedCompetitorGroups: {
    1: 'vc1',
    2: 'vc2',
  },
  competitorGroups: {
    byId: {
      vc1: {
        id: 'vc1',
        name: '',
        seriesId: '1',
        vehicles: [
          'vh1',
          'vh2',
          'vh3',
          'vh4',
          'vh5',
          'vh6',
          'vh7',
          'vh8',
          'vh9',
          'vh10',
          'vh11',
          'vh12',
          'vh13',
          'vh14',
          'vh15',
          'vh16',
          'vh17',
          'vh18',
          'vh19',
          'vh20',
          'vh21',
          'vh22',
          'vh23',
          'vh24',
          'vh25',
          'vh26',
          'vh27',
          'vh28',
          'vh29',
          'vh30',
          'vh31',
          'vh32',
          'vh33',
          'vh34',
          'vh35',
          'vh36',
        ],
      },
      vc2: {
        id: 'vc2',
        name: '',
        seriesId: '2',
        vehicles: ['vh37', 'vh38', 'vh39', 'vh40', 'vh41'],
      },
    },
    allIds: ['vc1', 'vc2'],
  },
  competitorGroupVehicles: {
    byId: {
      vh1: {
        id: 'vh1',
        vehicleNumber: '1',
        colorId: '30',
        highlighted: true,
      },
      vh2: {
        id: 'vh2',
        vehicleNumber: '3',
        colorId: '1',
        highlighted: true,
      },
      vh3: {
        id: 'vh3',
        vehicleNumber: '5',
        colorId: 'persianRose',
        highlighted: true,
      },
      vh4: {
        id: 'vh4',
        vehicleNumber: '8',
        colorId: '24',
        highlighted: true,
      },
      vh5: {
        id: 'vh5',
        vehicleNumber: '9',
        colorId: '17',
        highlighted: true,
      },
      vh6: {
        id: 'vh6',
        vehicleNumber: '24',
        colorId: 'burningOrange',
        highlighted: true,
      },
      vh7: {
        id: 'vh7',
        vehicleNumber: '99',
        colorId: 'royalBlue',
        highlighted: true,
      },
      vh8: {
        id: 'vh8',
        vehicleNumber: '48',
        colorId: 'bilbao',
        highlighted: true,
      },
      vh37: {
        id: 'vh37',
        vehicleNumber: '2',
        colorId: 'frenchPlum',
        highlighted: true,
      },
      vh38: {
        id: 'vh38',
        vehicleNumber: '19',
        colorId: 'purpleHeart',
        highlighted: true,
      },
      vh39: {
        id: 'vh39',
        vehicleNumber: '14',
        colorId: 'mulberry',
        highlighted: true,
      },
      vh40: {
        id: 'vh40',
        vehicleNumber: '35',
        colorId: 'radioactiveGreen',
        highlighted: true,
      },
      vh41: {
        id: 'vh41',
        vehicleNumber: '45',
        colorId: 'burningOrange',
        highlighted: true,
      },
      vh9: {
        id: 'vh9',
        vehicleNumber: '19',
        colorId: '33',
        highlighted: false,
      },
      vh10: {
        id: 'vh10',
        vehicleNumber: '2',
        colorId: '37',
        highlighted: false,
      },
      vh11: {
        id: 'vh11',
        vehicleNumber: '4',
        colorId: '37',
        highlighted: false,
      },
      vh12: {
        id: 'vh12',
        vehicleNumber: '6',
        colorId: '37',
        highlighted: false,
      },
      vh13: {
        id: 'vh13',
        vehicleNumber: '7',
        colorId: '35',
        highlighted: false,
      },
      vh14: {
        id: 'vh14',
        vehicleNumber: '12',
        colorId: '37',
        highlighted: false,
      },
      vh15: {
        id: 'vh15',
        vehicleNumber: '11',
        colorId: '33',
        highlighted: false,
      },
      vh16: {
        id: 'vh16',
        vehicleNumber: '10',
        colorId: '37',
        highlighted: false,
      },
      vh17: {
        id: 'vh17',
        vehicleNumber: '14',
        colorId: '37',
        highlighted: false,
      },
      vh18: {
        id: 'vh18',
        vehicleNumber: '15',
        colorId: '37',
        highlighted: false,
      },
      vh19: {
        id: 'vh19',
        vehicleNumber: '16',
        colorId: '35',
        highlighted: false,
      },
      vh20: {
        id: 'vh20',
        vehicleNumber: '17',
        colorId: '37',
        highlighted: false,
      },
      vh21: {
        id: 'vh21',
        vehicleNumber: '20',
        colorId: '33',
        highlighted: false,
      },
      vh22: {
        id: 'vh22',
        vehicleNumber: '21',
        colorId: '37',
        highlighted: false,
      },
      vh23: {
        id: 'vh23',
        vehicleNumber: '22',
        colorId: '37',
        highlighted: false,
      },
      vh24: {
        id: 'vh24',
        vehicleNumber: '23',
        colorId: '33',
        highlighted: false,
      },
      vh25: {
        id: 'vh25',
        vehicleNumber: '31',
        colorId: '35',
        highlighted: false,
      },
      vh26: {
        id: 'vh26',
        vehicleNumber: '34',
        colorId: '37',
        highlighted: false,
      },
      vh27: {
        id: 'vh27',
        vehicleNumber: '43',
        colorId: '37',
        highlighted: false,
      },
      vh28: {
        id: 'vh28',
        vehicleNumber: '42',
        colorId: '35',
        highlighted: false,
      },
      vh29: {
        id: 'vh29',
        vehicleNumber: '41',
        colorId: '37',
        highlighted: false,
      },
      vh30: {
        id: 'vh30',
        vehicleNumber: '38',
        colorId: '37',
        highlighted: false,
      },
      vh31: {
        id: 'vh31',
        vehicleNumber: '47',
        colorId: '35',
        highlighted: false,
      },
      vh32: {
        id: 'vh32',
        vehicleNumber: '45',
        colorId: '33',
        highlighted: false,
      },
      vh33: {
        id: 'vh33',
        vehicleNumber: '51',
        colorId: '37',
        highlighted: false,
      },
      vh34: {
        id: 'vh34',
        vehicleNumber: '78',
        colorId: '37',
        highlighted: false,
      },
      vh35: {
        id: 'vh35',
        vehicleNumber: '77',
        colorId: '35',
        highlighted: false,
      },
      vh36: {
        id: 'vh36',
        vehicleNumber: '54',
        colorId: '33',
        highlighted: false,
      },
    },
    allIds: [
      'vh1',
      'vh2',
      'vh3',
      'vh4',
      'vh5',
      'vh6',
      'vh7',
      'vh8',
      'vh37',
      'vh38',
      'vh39',
      'vh40',
      'vh41',
      'vh9',
      'vh10',
      'vh11',
      'vh12',
      'vh13',
      'vh14',
      'vh15',
      'vh16',
      'vh17',
      'vh18',
      'vh19',
      'vh20',
      'vh21',
      'vh22',
      'vh23',
      'vh24',
      'vh25',
      'vh26',
      'vh27',
      'vh28',
      'vh29',
      'vh30',
      'vh31',
      'vh32',
      'vh33',
      'vh34',
      'vh35',
      'vh36',
    ],
  },
  selectedReferenceVehicle: createEntity(),
  hideFooter: false,
  trackMapSettings: createEntity(),
};
